import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { Api, PublicUser } from "../api";
import { wavemindIcon } from "../utils/icons";
import { BaseElement } from "../utils/ui-components";
import { createRef, ref } from "lit/directives/ref.js";

@customElement("top-bar")
export class TopBar extends BaseElement {
    @property()
    user?: PublicUser;

    @state()
    private userMenuOpen = false;

    private menuRef = createRef<HTMLDivElement>();
    private buttonRef = createRef<HTMLButtonElement>();

    connectedCallback() {
        super.connectedCallback();
        document.addEventListener("click", this.handleDocumentClick);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener("click", this.handleDocumentClick);
    }

    private handleDocumentClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (!target.closest(".user-menu")) {
            this.userMenuOpen = false;
        }
    };

    private handleLogout = async () => {
        try {
            await Api.logout();
            location.href = "/";
        } catch (e) {
            console.error("Logout failed:", e);
        }
    };

    private renderMenu() {
        if (!this.userMenuOpen || !this.buttonRef.value) return null;

        const rect = this.buttonRef.value.getBoundingClientRect();
        const menuStyle = `
            position: fixed;
            top: ${rect.bottom + 8}px;
            right: ${window.innerWidth - rect.right}px;
        `;

        return html`
            <div class="fixed inset-0 z-[1000]" @click=${() => (this.userMenuOpen = false)}>
                <div
                    ${ref(this.menuRef)}
                    class="w-48 bg-white rounded-lg shadow-lg py-1"
                    style=${menuStyle}
                    @click=${(e: Event) => e.stopPropagation()}
                >
                    <a href="/user" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Profile</a>
                    ${this.user?.role === "admin"
                        ? html`<a href="/admin" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Admin Panel</a>`
                        : ""}
                    <button @click=${this.handleLogout} class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Log out</button>
                </div>
            </div>
        `;
    }

    render() {
        return html`
            <div class="h-12 bg-white border-b border-gray-200 flex items-center justify-between px-4 fixed top-0 left-0 right-0 z-10">
                <div class="text-blue-600">
                    <i class="w-12 h-12 icon">${wavemindIcon}</i>
                </div>

                <!-- User menu -->
                <div class="relative user-menu">
                    <button
                        ${ref(this.buttonRef)}
                        @click=${() => (this.userMenuOpen = !this.userMenuOpen)}
                        class="flex items-center gap-3 hover:bg-gray-50 py-1.5 px-3 rounded-lg transition-colors"
                    >
                        <div class="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
                            <svg class="w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="currentColor">
                                <path
                                    d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                                />
                            </svg>
                        </div>
                        ${this.user ? html`<span class="text-sm text-gray-700">${this.user.name}</span>` : ""}
                    </button>
                </div>
            </div>
            ${this.renderMenu()}
        `;
    }
}
